import { DEFAULT_MIN_ANIMATION_LOOP_SPEED } from 'components/admin/AnimationLoopSpeedField';
import React, { Reducer, useReducer, createContext } from 'react';
import { Case, Opinion, Feedback, QueueConfiguration } from 'services';

export interface CaseReview {
  case: Case;
  feedback: Feedback[];
  isSelected: boolean;
  opinion?: Opinion;
  topTrait?: string;
}

export interface ReviewContextState {
  cases: CaseReview[];
  timeElapsed: number;
  queue?: QueueConfiguration;
  nextImgCount: number;
  previousImgCount: number;
  carouselAnimationEnabled: boolean;
  carouselAnimationSpeed: number;
}

export type CaseReviewAction =
  | { type: 'CLEAR' | 'UNSELECT_ALL' | 'SELECT_ALL' }
  | { type: 'SETUP_REVIEW'; payload: { cases: Case[]; queue: QueueConfiguration } }
  | { type: 'SELECT_CASE' | 'UNSELECT_CASE' | 'RESET_OPINION'; payload: Case[] }
  | { type: 'SET_CASE_FEEDBACK'; caseId: string; payload: Feedback[] }
  | { type: 'PUT_OPINION'; payload: Opinion }
  | { type: 'SET_TIME_ELAPSED'; payload: number }
  | { type: 'UPDATE_NEXT_IMAGE_COUNT'; payload: number }
  | { type: 'UPDATE_PREVIOUS_IMAGE_COUNT'; payload: number }
  | { type: 'ENABLE_CAROUSEL_ANIMATION' }
  | { type: 'DISABLE_CAROUSEL_ANIMATION' }
  | { type: 'SET_ANIMATION_LOOP_SPEED'; payload: number };

function createCaseReview(cases: Case[]): CaseReview[] {
  return cases.map(item => {
    return {
      case: item,
      feedback: [],
      isSelected: false,
      opinion: undefined
    };
  });
}

export const reviewReducer: Reducer<ReviewContextState, CaseReviewAction> = (
  state: ReviewContextState,
  action: CaseReviewAction
): ReviewContextState => {
  switch (action.type) {
    case 'SETUP_REVIEW':
      return {
        ...state,
        cases: [...createCaseReview(action.payload.cases)],
        queue: action.payload.queue,
        timeElapsed: 0,
        nextImgCount: 0,
        previousImgCount: 0
      };
    case 'CLEAR':
      return {
        cases: [],
        timeElapsed: 0,
        queue: undefined,
        nextImgCount: 0,
        previousImgCount: 0,
        carouselAnimationEnabled: false,
        carouselAnimationSpeed: DEFAULT_MIN_ANIMATION_LOOP_SPEED
      };
    case 'SELECT_CASE':
      return {
        ...state,
        cases: state.cases.map(item => {
          if (action.payload.some(val => val.id === item.case.id)) {
            item.isSelected = true;
          }
          return item;
        })
      };
    case 'SELECT_ALL':
      return {
        ...state,
        cases: state.cases.map(item => {
          if (item.opinion === undefined) {
            item.isSelected = true;
          }
          return item;
        })
      };
    case 'UNSELECT_ALL':
      return {
        ...state,
        cases: state.cases.map(item => {
          item.isSelected = false;
          return item;
        })
      };
    case 'UNSELECT_CASE':
      return {
        ...state,
        cases: state.cases.map(item => {
          if (action.payload.some(val => val.id === item.case.id)) {
            item.isSelected = false;
          }
          return item;
        })
      };
    case 'PUT_OPINION':
      return {
        ...state,
        cases: state.cases.map(item => {
          if (item.isSelected) {
            item.opinion = action.payload;
          }
          return item;
        })
      };
    case 'RESET_OPINION':
      return {
        ...state,
        cases: state.cases.map(item => {
          if (action.payload.some(val => val.id === item.case.id)) {
            item.opinion = undefined;
          }
          return item;
        })
      };
    case 'SET_CASE_FEEDBACK':
      return {
        ...state,
        cases: state.cases.map(item => {
          if (item.case.id === action.caseId) {
            item.feedback = action.payload;
          }
          return item;
        })
      };
    case 'SET_TIME_ELAPSED':
      return {
        ...state,
        timeElapsed: action.payload
      };
    case 'UPDATE_NEXT_IMAGE_COUNT':
      return {
        ...state,
        nextImgCount: action.payload
      };
    case 'UPDATE_PREVIOUS_IMAGE_COUNT':
      return {
        ...state,
        previousImgCount: action.payload
      };
    case 'ENABLE_CAROUSEL_ANIMATION':
      return {
        ...state,
        carouselAnimationEnabled: true
      };
    case 'DISABLE_CAROUSEL_ANIMATION':
      return {
        ...state,
        carouselAnimationEnabled: false
      };
    case 'SET_ANIMATION_LOOP_SPEED':
      return {
        ...state,
        carouselAnimationSpeed: action.payload
      };
    default:
      return state;
  }
};

export interface ReviewContextProps {
  state: ReviewContextState;
  dispatch: (action: CaseReviewAction) => void;
}

export const ReviewContext = createContext<ReviewContextProps>({} as ReviewContextProps);

export const ReviewContextProvider: React.FunctionComponent<Record<string, unknown>> = props => {
  const [state, dispatch] = useReducer(reviewReducer, {
    cases: [],
    timeElapsed: 0,
    nextImgCount: 0,
    previousImgCount: 0,
    carouselAnimationEnabled: false,
    carouselAnimationSpeed: DEFAULT_MIN_ANIMATION_LOOP_SPEED
  });
  return <ReviewContext.Provider value={{ state, dispatch }}>{props.children}</ReviewContext.Provider>;
};
